import { ReactNode, Suspense } from 'react';

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Outlet, Route, Routes } from 'react-router-dom';

import { useSetAnalyticsGlobal } from '@cast/analytics';
import { DrawerProvider } from '@cast/design-system';
import { lazyRetry, getDocsLink } from '@cast/utils';

import { ContactsForm } from 'components/dialogs';
import { SuspenseLoader } from 'components/loaders';
import { RouteExclude } from 'components/RouteExclude/RouteExclude';
import {
  Navigate,
  NavigatePreservingSearch,
  ProtectedRoute,
  NavigateWithPathnameReplace,
  NavigateWithPathnamePrefix,
  ReadOnlyProtectedRoute,
} from 'components/router';
import {
  ACTIVITY_PATHS,
  BASE_PATHS,
  PRODUCT_BASE_PATHS,
} from '@cast/console-constants';
import { AbilityGuard, useAbility } from '@cast/shared-ability-data-access';
import { TourProvider } from 'core/tour';
import { TourProvider as TourProviderV2 } from 'core/tour-v2/TourProvider';
import { OpinionatedPlatformTour } from 'features/demo/OpinionatedPlatformTour';
import { CriticalNotificationList } from 'features/notifications/CriticalNotificationList';
import { useIsOrganizationAuditLogVisible } from 'hooks/permissions/useIsOrganizationAuditLogVisible';
import { useSentryRouteTagging } from 'monitoring/useSentryRouteTracking';
import { DemoController } from 'pages/DemoController';
import { ForbiddenPage } from 'pages/error/Forbidden';

import { useContactsForm } from './components/dialogs/contacts-form/_hooks';
import { ErrorPage } from './components/error-pages';
import { LandingRedirect } from './LandingRedirect';
import { MainLayout as MainLayoutOp } from './main-layout-op';
import { OrganizationSettingsRedirect } from './pages/organization/OrganizationSettingsRedirect';

dayjs.extend(isBetween);
dayjs.extend(isToday);

dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(timezone);

const ClusterController = lazyRetry(
  () => import('pages/cluster/ClusterController')
);

const OrganizationController = lazyRetry(
  () => import('pages/organization/OrganizationController')
);

const ApiAccessKeysController = lazyRetry(
  () => import('pages/api-access-keys/ApiAccessKeysController')
);

const NotificationsController = lazyRetry(
  () => import('pages/notifications/NotificationsController')
);

const YamlPreviewPage = lazyRetry(
  () => import('pages/yaml-preview/YamlPreviewPage')
);

const OptimizationController = lazyRetry(
  () => import('pages/OptimizationController')
);

const DBOptimizationController = lazyRetry(
  () => import('pages/db-optimization/DbOptimizationController')
);

const DBOptimizationControllerV2 = lazyRetry(
  () =>
    import(
      'pages/db-optimization/v2/DbOptimizationControllerV2/DbOptimizationControllerV2'
    )
);

const OrganizationEnterpriseController = lazyRetry(
  () => import('pages/organization/enterprise/OrganizationEnterpriseController')
);

const OrganizationSecurityController = lazyRetry(
  () =>
    import(
      'pages/organization/security-insights/OrganizationSecurityController'
    )
);

const ApaController = lazyRetry(() => import('pages/apa/ApaController'));

const CloudVmsController = lazyRetry(
  () => import('pages/cloud-vms/CloudVmsController')
);

const OrganizationAuditLogController = lazyRetry(
  () => import('pages/organization/audit-log/OrganizationAuditLogController')
);

// makes diff smaller when new global context is added
function GlobalContexts({ children }: { children: ReactNode }) {
  return (
    <TourProviderV2>
      <TourProvider>
        <DrawerProvider>
          <OpinionatedPlatformTour />
          {children}
        </DrawerProvider>
      </TourProvider>
    </TourProviderV2>
  );
}

export const App = () => {
  const { showForm, submitContactsForm, closeContactsForm } = useContactsForm();
  const isDboV2Enabled = useAbility().can('view', 'FeatureDboV2');
  useSentryRouteTagging();

  useSetAnalyticsGlobal('isOpinionatedPlatformEnabled', true);
  const isOrgAuditLogEnabled = useIsOrganizationAuditLogVisible();

  return (
    <GlobalContexts>
      <Routes>
        <Route
          path="/"
          element={
            <MainLayoutOp>
              <AbilityGuard subject="OrganizationNotifications" action="view">
                <RouteExclude
                  paths={[
                    BASE_PATHS.notifications,
                    ACTIVITY_PATHS.activityNotifications,
                    PRODUCT_BASE_PATHS.dbo,
                  ]}
                >
                  <CriticalNotificationList />
                </RouteExclude>
              </AbilityGuard>
              <Suspense fallback={<SuspenseLoader />}>
                {showForm ? (
                  <ContactsForm
                    submitContactsForm={submitContactsForm}
                    closeContactsForm={closeContactsForm}
                  />
                ) : (
                  <Outlet />
                )}
              </Suspense>
            </MainLayoutOp>
          }
        >
          <Route index Component={LandingRedirect} />

          <Route
            path="/dashboard/*"
            element={
              <Navigate to={`${PRODUCT_BASE_PATHS.automation}/cluster-list`} />
            }
          />

          <Route
            path={`${PRODUCT_BASE_PATHS.reporting}/*`}
            element={
              <NavigateWithPathnameReplace
                searchValue={/^\/reporting/}
                replaceValue={PRODUCT_BASE_PATHS.automation}
                preserveSearch
              />
            }
          />

          <Route element={<ReadOnlyProtectedRoute />}>
            <Route
              element={
                <ProtectedRoute subject="OrganizationRebalanceSchedules" />
              }
            >
              <Route
                path="/settings/*"
                element={
                  <NavigateWithPathnameReplace
                    searchValue="/settings"
                    replaceValue={`${PRODUCT_BASE_PATHS.automation}/schedules`}
                    preserveSearch
                  />
                }
              />
            </Route>
          </Route>

          <Route
            element={<ProtectedRoute subject="OrganizationNotifications" />}
          >
            <Route
              path={`${ACTIVITY_PATHS.activityNotifications}/*`}
              element={<NotificationsController />}
            />
            {/* Redirect old /notifications/* → /activity/notifications/* */}
            <Route
              path={`${BASE_PATHS.notifications}/*`}
              element={
                <NavigateWithPathnameReplace
                  searchValue={BASE_PATHS.notifications}
                  replaceValue={ACTIVITY_PATHS.activityNotifications}
                />
              }
            />
          </Route>

          <Route
            path={PRODUCT_BASE_PATHS.settings}
            element={<OrganizationSettingsRedirect />}
          />

          <Route
            path={`${PRODUCT_BASE_PATHS.settings}/enterprise/:enterpriseId/*`}
            element={<OrganizationEnterpriseController />}
          />

          <Route
            path={`${PRODUCT_BASE_PATHS.settings}/*`}
            element={<OrganizationController />}
          />

          <Route element={<ReadOnlyProtectedRoute />}>
            <Route
              element={<ProtectedRoute subject="OrganizationCommitments" />}
            >
              <Route
                path="/commitments/*"
                element={
                  <NavigateWithPathnamePrefix
                    prefix={PRODUCT_BASE_PATHS.automation}
                  />
                }
              />
            </Route>
          </Route>

          {isOrgAuditLogEnabled && (
            <>
              <Route
                path={`${ACTIVITY_PATHS.activityEvents}/*`}
                element={<OrganizationAuditLogController />}
              />
              {/* /activity → /activity/events */}
              <Route
                path={ACTIVITY_PATHS.activity}
                element={
                  <NavigatePreservingSearch
                    to={ACTIVITY_PATHS.activityEvents}
                  />
                }
              />
              {/* Redirect old /audit-log/* → /activity/events/* */}
              <Route
                path={`${PRODUCT_BASE_PATHS.auditLog}/*`}
                element={
                  <NavigateWithPathnameReplace
                    searchValue={PRODUCT_BASE_PATHS.auditLog}
                    replaceValue={ACTIVITY_PATHS.activityEvents}
                  />
                }
              />
            </>
          )}

          <Route element={<ProtectedRoute subject="OrganizationDbOptimizer" />}>
            <Route
              path={`${PRODUCT_BASE_PATHS.dbo}/*`}
              element={
                isDboV2Enabled ? (
                  <DBOptimizationControllerV2 />
                ) : (
                  <DBOptimizationController />
                )
              }
            />
          </Route>

          <Route
            path={`${PRODUCT_BASE_PATHS.security}/*`}
            element={<OrganizationSecurityController />}
          />

          <Route element={<ReadOnlyProtectedRoute />}>
            <Route
              element={
                <ProtectedRoute subject="OrganizationNetworkIntelligence" />
              }
            >
              <Route
                path="/network-intelligence/*"
                element={
                  <NavigateWithPathnamePrefix
                    prefix={PRODUCT_BASE_PATHS.automation}
                  />
                }
              />
            </Route>
          </Route>

          <Route
            path={`${PRODUCT_BASE_PATHS.apa}/*`}
            element={<ApaController />}
          />

          <Route
            path="/automation/cloud-vms/*"
            element={<CloudVmsController />}
          />

          <Route element={<ReadOnlyProtectedRoute />}>
            <Route element={<ProtectedRoute subject="Clusters" />}>
              <Route
                path="/external-clusters/:clusterId/*"
                element={<ClusterController />}
              />
            </Route>
          </Route>

          <Route
            path="/user/api-access/*"
            element={<ApiAccessKeysController />}
          />

          <Route
            path={`${PRODUCT_BASE_PATHS.automation}/*`}
            element={<OptimizationController />}
          />

          <Route path="/yaml-preview" element={<YamlPreviewPage />} />

          <Route path="/demo" element={<DemoController />} />
        </Route>

        {/* Redirect to https://docs.cast.ai/docs */}
        <Route
          path="/docs"
          Component={() => {
            window.location.href = getDocsLink('docs');
            return null;
          }}
        />

        <Route
          path="/forbidden"
          element={
            <MainLayoutOp noSidebar>
              <ForbiddenPage />
            </MainLayoutOp>
          }
        />

        <Route
          path="*"
          element={
            <MainLayoutOp>
              <ErrorPage errorStatus={404} />
            </MainLayoutOp>
          }
        />
      </Routes>
    </GlobalContexts>
  );
};
